/**
 * <delivery-estimator>
 *
 * Renders a pincode field; on submit, asks the Amkette Delivery-EDD backend
 * service for an estimated delivery date and shows it. The endpoint URL is read
 * from the data-endpoint attribute (public — no secrets in the theme).
 *
 * Fails safe: any network/error simply shows a neutral message and never blocks
 * the Add-to-Cart flow.
 */
if (!customElements.get('delivery-estimator')) {
  customElements.define(
    'delivery-estimator',
    class DeliveryEstimator extends HTMLElement {
      constructor() {
        super();
        this.endpoint = this.dataset.endpoint;
        this.cutoffNote = this.dataset.cutoffNote || '';
        this.form = this.querySelector('.delivery-estimator__form');
        this.input = this.querySelector('.delivery-estimator__input');
        this.result = this.querySelector('.delivery-estimator__result');
        this.button = this.querySelector('.delivery-estimator__button');
        this.storageKey = 'amkette_delivery_pincode';
      }

      connectedCallback() {
        if (!this.endpoint || !this.form) return;

        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.check();
        });

        // Keep the field numeric-only.
        this.input.addEventListener('input', () => {
          this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
        });

        // Auto-fill and auto-check from the customer's last-used pincode.
        const saved = this.readSavedPincode();
        if (saved) {
          this.input.value = saved;
          this.check({ silent: true });
        }
      }

      readSavedPincode() {
        try {
          const v = window.localStorage.getItem(this.storageKey);
          return /^\d{6}$/.test(v || '') ? v : '';
        } catch (_) {
          return '';
        }
      }

      savePincode(pin) {
        try {
          window.localStorage.setItem(this.storageKey, pin);
        } catch (_) {
          /* private mode / disabled storage — ignore */
        }
      }

      setResult(html, state) {
        this.result.hidden = false;
        this.result.className = 'delivery-estimator__result';
        if (state) this.result.classList.add('delivery-estimator__result--' + state);
        this.result.innerHTML = html;
      }

      async check({ silent = false } = {}) {
        const pincode = (this.input.value || '').trim();

        if (!/^\d{6}$/.test(pincode)) {
          if (!silent) this.setResult('Please enter a valid 6-digit pincode.', 'error');
          return;
        }

        this.savePincode(pincode);
        this.button.disabled = true;
        this.setResult('Checking…', 'loading');

        try {
          const url = this.endpoint + (this.endpoint.includes('?') ? '&' : '?') + 'pincode=' + encodeURIComponent(pincode);
          const res = await fetch(url, { headers: { Accept: 'application/json' } });

          if (!res.ok) throw new Error('bad status ' + res.status);
          const data = await res.json();

          if (data.serviceable === false) {
            this.setResult("Sorry, we don't deliver to this pincode yet.", 'error');
            return;
          }

          const label = data.delivery_label || (data.delivery_date ? 'Get it by ' + data.delivery_date : '');
          if (!label) throw new Error('missing label');

          let html = '<strong class="delivery-estimator__date">' + this.escape(label) + '</strong>';
          if (data.courier) {
            html += '<span class="delivery-estimator__courier"> via ' + this.escape(data.courier) + '</span>';
          }
          if (this.cutoffNote) {
            html += '<span class="delivery-estimator__cutoff">' + this.escape(this.cutoffNote) + '</span>';
          }
          this.setResult(html, 'success');
        } catch (err) {
          // Fail safe — never block the purchase flow.
          if (silent) {
            this.result.hidden = true;
          } else {
            this.setResult('Delivery estimate is unavailable right now. Please try again later.', 'error');
          }
        } finally {
          this.button.disabled = false;
        }
      }

      escape(str) {
        return String(str).replace(/[&<>"']/g, (c) => {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }
    }
  );
}
